import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { type AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance, API_SCOPES } from './msalConfig'
import { getRolesFromAccessToken, type Roles } from './roles'

// ---------------------------------------------------------------------------
// Auth state shape
// ---------------------------------------------------------------------------
//
// `authenticated` carries `roles` because role information lives on the
// **access token**, not the ID token (App Roles emit on the audience they
// are assigned on — that's `sp-tis-p-orion-api`, the API). We acquire the
// access token as part of auth init and decode its `roles` claim into the
// same shape used by the UI gates. Subsequent role reads are synchronous.

export type AuthState =
  | { status: 'initialising' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; account: AccountInfo; roles: Roles }

export interface AuthContextValue {
  authState: AuthState
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  /** Acquires a bearer token silently. Falls back to interactive on InteractionRequiredAuthError. */
  getToken: () => Promise<string>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({ status: 'initialising' })

  useEffect(() => {
    // initialize() + handleRedirectPromise() must complete before we read any
    // account state. After that we try, in order:
    //   1. A cached account from sessionStorage (a prior page load or the
    //      redirect-result we just processed).
    //   2. Silent SSO against the user's existing Entra tenant session
    //      (e.g. they're already signed in to Outlook / SharePoint / Teams
    //      in this browser). If the browser has the session cookie or the
    //      Microsoft SSO extension, this returns a fresh account with no UI.
    //   3. If both fail, transition to `unauthenticated` so the sign-in
    //      button renders.
    //
    // ssoSilent failure modes are deliberately swallowed: there's no useful
    // signal for the user beyond "you'll need to click sign in." The most
    // common failure paths are no tenant session in the browser, third-
    // party-cookie restrictions (Safari ITP), and Conditional Access policies
    // that require a fresh interactive prompt.
    let cancelled = false

    async function bootAuth(): Promise<void> {
      await msalInstance.initialize()
      await msalInstance.handleRedirectPromise()
      if (cancelled) return

      // Path 1: cached account.
      const cached = msalInstance.getAllAccounts()[0]
      if (cached) {
        msalInstance.setActiveAccount(cached)
        await acquireAndSetAuthenticated(cached)
        return
      }

      // Path 2: silent SSO using the existing tenant session in the browser.
      // The result already includes the access token we need — decode roles
      // directly to avoid a second round trip.
      try {
        const ssoResult = await msalInstance.ssoSilent({ scopes: API_SCOPES })
        if (cancelled) return
        if (ssoResult.account) {
          msalInstance.setActiveAccount(ssoResult.account)
          const roles = getRolesFromAccessToken(ssoResult.accessToken)
          setAuthState({ status: 'authenticated', account: ssoResult.account, roles })
          return
        }
      } catch {
        // ssoSilent failed (no session, interaction required, blocked
        // cookies). Quietly fall through to unauthenticated; the user
        // clicks Sign in.
      }
      if (cancelled) return

      setAuthState({ status: 'unauthenticated' })
    }

    async function acquireAndSetAuthenticated(account: AccountInfo): Promise<void> {
      try {
        const result = await msalInstance.acquireTokenSilent({
          scopes: API_SCOPES,
          account,
        })
        if (cancelled) return
        const roles = getRolesFromAccessToken(result.accessToken)
        setAuthState({ status: 'authenticated', account, roles })
      } catch (err) {
        if (cancelled) return
        if (err instanceof InteractionRequiredAuthError) {
          // Silent acquire failed — recover via redirect. The redirect
          // navigates away; the next page load will re-enter this effect.
          await msalInstance.acquireTokenRedirect({ scopes: API_SCOPES, account })
          return
        }
        // Some other failure (network, configuration). Surface as
        // unauthenticated so the user can retry sign-in. The token we'd
        // have decoded for roles is unavailable; treat as no-role.
        setAuthState({ status: 'unauthenticated' })
      }
    }

    // Without this catch, any throw inside bootAuth (most commonly an
    // error coming back in the redirect hash from a failed/denied
    // acquireTokenRedirect) leaves authState stuck on 'initialising'
    // and the SPA shows STARTING… forever. Surfacing as
    // 'unauthenticated' gives the user a Sign-in button to retry.
    bootAuth().catch(err => {
      if (cancelled) return
      // eslint-disable-next-line no-console
      console.error('Auth boot failed:', err)
      setAuthState({ status: 'unauthenticated' })
    })

    return () => {
      cancelled = true
    }
  }, [])

  const signIn = async () => {
    await msalInstance.loginRedirect({ scopes: API_SCOPES })
  }

  const signOut = async () => {
    const account = authState.status === 'authenticated' ? authState.account : null
    setAuthState({ status: 'unauthenticated' })
    await msalInstance.logoutRedirect({ account })
  }

  const getToken = async (): Promise<string> => {
    const account = authState.status === 'authenticated' ? authState.account : undefined
    if (!account) throw new Error('Not authenticated')

    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: API_SCOPES,
        account,
      })
      return result.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Silent acquisition failed (expired refresh token, Conditional Access
        // policy change, etc.). Kick off an interactive redirect to recover.
        await msalInstance.acquireTokenRedirect({ scopes: API_SCOPES, account })
        // acquireTokenRedirect navigates away; this line is never reached.
        throw new Error('Redirecting for interactive auth')
      }
      throw err
    }
  }

  return (
    <AuthContext.Provider value={{ authState, signIn, signOut, getToken }}>
      {children}
    </AuthContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider')
  return ctx
}
